/**
 * Preserves scroll position when switching between profile tabs.
 *
 * Profile tabs are sibling routes, so the entire UserProfilePage
 * component tree unmounts and remounts on tab switch, causing the
 * browser to lose scroll position. This module stores the scroll
 * position before navigation and restores it after the new tab mounts.
 *
 * The value is cleared lazily (on next macrotask) rather than on read,
 * because some tabs trigger an intermediate redirect (e.g. /assets →
 * /assets/coupons) that causes a second remount before the scroll
 * restore from the first mount is visible.
 */
let savedScrollY: number | null = null

export function saveProfileScroll() {
  savedScrollY = window.scrollY
}

export function consumeProfileScroll(): number | null {
  const value = savedScrollY
  if (value != null) {
    // Clear asynchronously so the value survives intermediate redirects
    // that cause additional unmount/remount cycles within the same tick.
    setTimeout(() => {
      savedScrollY = null
    }, 0)
  }
  return value
}

/**
 * Navigates to a sub-tab URL while preserving scroll position.
 *
 * Saves the current scroll position, then navigates. The saved value
 * is consumed by the useLayoutEffect in UserProfilePage which runs
 * synchronously before paint, preventing any visible scroll jump.
 */
export function navigateWithScrollLock(
  navigate: (to: string, opts?: { preventScrollReset?: boolean }) => void,
  to: string,
) {
  saveProfileScroll()
  navigate(to, { preventScrollReset: true })
}
